import { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  FileCheck, Upload, CheckCircle2, Trash2, Download, History, X,
  AlertTriangle, FileText, Shield, FolderClosed, Eye, Calendar, Send,
  Sparkles, Loader2, UserCheck
} from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";
import { CmsCoverageCollapsibleSection } from "../components/admin/CmsCoverageCollapsibleSection";
import { LegalCoverageGapsAlert } from "../components/admin/LegalCoverageGapsAlert";

const TYPE_META = {
  privacy: { label: "Informativa Privacy", icon: Shield, accent: "blue" },
  contract: { label: "Contratto", icon: FileText, accent: "amber" },
  annex: { label: "Allegati", icon: FolderClosed, accent: "purple" },
};

// Display labels for the product axis used by contracts. Keep in sync
// with the backend ALLOWED_PRODUCTS — codes are sent over the wire
// verbatim (no translation table).
const PRODUCT_LABELS = {
  __generic__: "Generico (tutti i prodotti)",
  "Passive 1": "Track & Trace GPS 1",
  "Passive 3": "Track & Trace GPS 3",
  "Passive 4": "Track & Trace GPS 4",
};

const LANG_LABELS = {
  it: "Italiano", en: "English", de: "Deutsch", fr: "Français",
  es: "Español", pt: "Português", nl: "Nederlands", pl: "Polski",
  cs: "Čeština", sk: "Slovenčina", hu: "Magyar", ro: "Română",
  bg: "Български", el: "Ελληνικά", hr: "Hrvatski", sl: "Slovenščina",
  da: "Dansk", sv: "Svenska",
  no: "Norsk", fi: "Suomi",  // future SOC expansion
};

// ─── SOC country view (Mara CR 04/07/2026) ────────────────────────
// The tile grid is keyed by SOC country instead of language. Each
// country card resolves to a specific PDF via `COUNTRY_TO_LANGUAGE`
// (populated by the /coverage endpoint), so UK/IRL/GBZ tiles all
// point to the English PDF while remaining visually distinct.
const COUNTRY_META = {
  UK:  { flag: "🇬🇧", name: "United Kingdom" },
  IT:  { flag: "🇮🇹", name: "Italia" },
  ES:  { flag: "🇪🇸", name: "España" },
  FR:  { flag: "🇫🇷", name: "France" },
  NL:  { flag: "🇳🇱", name: "Nederland" },
  BE:  { flag: "🇧🇪", name: "Belgique" },
  PT:  { flag: "🇵🇹", name: "Portugal" },
  PL:  { flag: "🇵🇱", name: "Polska" },
  SE:  { flag: "🇸🇪", name: "Sverige" },
  NO:  { flag: "🇳🇴", name: "Norge" },
  FI:  { flag: "🇫🇮", name: "Suomi" },
  DK:  { flag: "🇩🇰", name: "Danmark" },
  DE:  { flag: "🇩🇪", name: "Deutschland" },
  CZ:  { flag: "🇨🇿", name: "Česko" },
  GR:  { flag: "🇬🇷", name: "Ελλάδα" },
  CH:  { flag: "🇨🇭", name: "Schweiz" },
  GBZ: { flag: "🇬🇮", name: "Gibraltar" },
  IRL: { flag: "🇮🇪", name: "Éire" },
};

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString('it-IT', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }); }
  catch { return iso; }
};

const fmtSize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

// ──────────────────── Upload modal ────────────────────

const UploadModal = ({ defaultType, defaultLang, defaultCountry, defaultProduct, supportedTypes, supportedLanguages, supportedProducts, productAwareTypes, countryToLanguage, countryLanguages, onClose, onUploaded, getAuthHeader }) => {
  const [docType, setDocType] = useState(defaultType || "privacy");
  const [language, setLanguage] = useState(defaultLang || "it");
  // Zorzutti CR 29/07/2026 — the admin can now scope the upload to a
  // SPECIFIC country. When ``country`` is empty the upload behaves as
  // before (applies to every country that shares this language via
  // COUNTRY_TO_LANGUAGE). When a country is chosen the record is
  // stored with ``country=XX`` and only THAT country's customers will
  // resolve to it — everyone else keeps the country=null legacy doc.
  const [country, setCountry] = useState(defaultCountry || "");
  const [product, setProduct] = useState(defaultProduct || "__generic__");
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [activateNow, setActivateNow] = useState(true);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showLocalPreview, setShowLocalPreview] = useState(false);
  const fileRef = useRef(null);

  // Fabrizio CR 03/07/2026 — Anteprima pre-upload: URL.createObjectURL su
  // File locale così Zorzutti può verificare il PDF PRIMA di inviarlo al
  // server (cattura errori tipo PDF vuoto/corrotto/password-protected).
  const localBlobUrl = useMemo(() => {
    if (!file) return null;
    return URL.createObjectURL(file);
  }, [file]);
  useEffect(() => {
    return () => { if (localBlobUrl) URL.revokeObjectURL(localBlobUrl); };
  }, [localBlobUrl]);

  const isProductAware = (productAwareTypes || []).includes(docType);

  // ─── SOC Country coverage info (Mara CR 04/07/2026 — bulk UX) ────
  // Elenco dei paesi SOC che condivideranno lo stesso PDF via
  // COUNTRY_TO_LANGUAGE. Il record MongoDB è singolo (keyed by
  // language) ma nella UI mostriamo esplicitamente l'elenco dei paesi
  // coperti così l'admin sa che caricando "en" copre UK + IRL + GBZ
  // senza dover fare 3 upload separati.
  const sameLangCountries = useMemo(() => {
    const map = countryToLanguage || {};
    return Object.keys(map)
      .filter(c => map[c] === language)
      .sort();
  }, [countryToLanguage, language]);
  const isMultiCountry = sameLangCountries.length >= 2;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Seleziona un file PDF"); return; }
    if (!country) { toast.error("Seleziona il paese di destinazione"); return; }
    if (!version.trim()) { toast.error("Indica una versione (es. 1.0)"); return; }
    setSubmitting(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    fd.append("language", language);
    if (country) fd.append("country", country);
    fd.append("version", version.trim());
    // Only send the product when the type is product-aware AND a
    // specific product was chosen (not the generic fallback).
    if (isProductAware && product && product !== "__generic__") {
      fd.append("product", product);
    }
    if (notes.trim()) fd.append("notes", notes.trim());
    fd.append("activate_now", activateNow ? "true" : "false");
    try {
      await axios.post(`${API}/admin/legal-documents/upload`, fd, {
        ...getAuthHeader(),
        headers: { ...(getAuthHeader().headers || {}), "Content-Type": "multipart/form-data" },
      });
      toast.success(activateNow ? "Caricato e attivato" : "Caricato come bozza");
      onUploaded();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-lg border border-gray-700 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="legal-upload-modal">
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <div className="flex items-center gap-3"><Upload size={20} className="text-blue-400" /><h2 className="text-lg font-bold text-white">Carica documento legale</h2></div>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg"><X size={20} className="text-gray-400" /></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs">Tipologia*</label>
              <select value={docType} onChange={e => setDocType(e.target.value)} className="w-full mt-1 px-3 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" data-testid="upload-type">
                {supportedTypes.map(t => <option key={t} value={t} style={{backgroundColor:'#111'}}>{TYPE_META[t]?.label || t}</option>)}
              </select>
            </div>
            <div>
              {/* Zorzutti CR 29/07/2026 — country is now MANDATORY.
                  Each SOC country has its own contract & privacy notice
                  (FR consumer law ≠ BE consumer law even if the language
                  matches). For multilingual countries the language
                  becomes a second dropdown (BE→FR/NL/DE, CH→DE/FR/IT,
                  FI→FI/SV, IRL→EN/GA, GBZ→EN/ES) so Legal can upload
                  a dedicated variant per official language. */}
              <label className="text-gray-400 text-xs">Paese*</label>
              <select
                value={country}
                onChange={e => {
                  const cc = e.target.value;
                  setCountry(cc);
                  const accepted = (countryLanguages || {})[cc] || [];
                  if (accepted.length) setLanguage(accepted[0]);
                }}
                className="w-full mt-1 px-3 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none"
                data-testid="upload-country"
                required
              >
                <option value="" style={{backgroundColor:'#111'}}>Seleziona paese…</option>
                {Object.keys(countryToLanguage || {}).sort().map(cc => {
                  const meta = COUNTRY_META[cc];
                  const langs = (countryLanguages || {})[cc] || [];
                  const badge = langs.length > 1 ? ` · ${langs.length} lingue` : "";
                  return (
                    <option key={cc} value={cc} style={{backgroundColor:'#111'}}>
                      {meta?.flag || "🏳️"} {meta?.name || cc} ({cc}){badge}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {country && (
            <div>
              {(() => {
                const accepted = (countryLanguages || {})[country] || [];
                const isBilingual = accepted.length > 1;
                if (isBilingual) {
                  return (
                    <>
                      <label className="text-gray-400 text-xs">
                        Lingua* <span className="text-lojack-blue-300/80 text-[10px]">— paese multilingua ({accepted.join(" · ")})</span>
                      </label>
                      <select
                        value={language}
                        onChange={e => setLanguage(e.target.value)}
                        className="w-full mt-1 px-3 py-2.5 bg-[#111] border border-lojack-blue-500/50 rounded-xl text-white focus:border-lojack-blue-400 focus:outline-none"
                        data-testid="upload-language-per-country"
                      >
                        {accepted.map(l => (
                          <option key={l} value={l} style={{backgroundColor:'#111'}}>
                            {LANG_LABELS[l] || l} ({l})
                          </option>
                        ))}
                      </select>
                      <p className="text-[10px] text-gray-500 mt-1">
                        Il cliente riceverà questa versione se ha impostato <code className="text-gray-400">{language}</code> come lingua preferita nel suo profilo. In assenza di preferenza, riceve la lingua canonica del paese ({accepted[0]}).
                      </p>
                    </>
                  );
                }
                return (
                  <p className="text-[10px] text-gray-500">
                    Lingua auto-derivata: <strong className="text-gray-300">{LANG_LABELS[language] || language.toUpperCase()}</strong>
                  </p>
                );
              })()}
            </div>
          )}
          {country && (
            <div className="p-3 bg-lojack-blue-500/10 border border-lojack-blue-500/30 rounded-xl" data-testid="upload-country-summary">
              <p className="text-lojack-blue-200 text-xs leading-relaxed">
                <strong>🎯 Ambito:</strong> questo documento verrà applicato <strong>ESCLUSIVAMENTE al paese {country}</strong>{" "}
                ({COUNTRY_META[country]?.name || country}). Gli altri paesi con la stessa lingua continueranno a usare la loro versione dedicata.
              </p>
            </div>
          )}
          {isProductAware && (
            <div data-testid="upload-product-block">
              <label className="text-gray-400 text-xs">Prodotto commerciale</label>
              <select value={product} onChange={e => setProduct(e.target.value)} className="w-full mt-1 px-3 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" data-testid="upload-product">
                <option value="__generic__" style={{backgroundColor:'#111'}}>{PRODUCT_LABELS["__generic__"]}</option>
                {(supportedProducts || []).map(p => (
                  <option key={p} value={p} style={{backgroundColor:'#111'}}>{PRODUCT_LABELS[p] || p}</option>
                ))}
              </select>
              <p className="text-gray-500 text-[11px] mt-1">
                Scegli il prodotto specifico (Passive 1/3/4) se il testo è dedicato a quella variante. Lascia <strong>“Generico”</strong> per il template di fallback usato quando non esiste una versione specifica.
              </p>
            </div>
          )}
          <div>
            <label className="text-gray-400 text-xs">Versione*</label>
            <input value={version} onChange={e => setVersion(e.target.value)} placeholder="1.0  ·  2026-Q2  ·  rev-B" className="w-full mt-1 px-3 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" data-testid="upload-version" required />
          </div>
          <div>
            <label className="text-gray-400 text-xs">Note (opzionale)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Es. Versione preliminare, mancano consensi finali" rows={2} className="w-full mt-1 px-3 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none resize-none" data-testid="upload-notes" />
          </div>
          <div>
            <label className="text-gray-400 text-xs">File PDF* (max 10 MB)</label>
            <div className="mt-1">
              <input ref={fileRef} type="file" accept="application/pdf,.pdf" onChange={e => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="upload-file" />
              <div className="flex gap-2">
                <button type="button" onClick={() => fileRef.current?.click()} className="flex-1 px-3 py-3 bg-[#111] border-2 border-dashed border-gray-700 hover:border-blue-500/50 rounded-xl text-gray-400 hover:text-blue-400 transition-colors flex items-center justify-center gap-2 text-sm">
                  <Upload size={16} /> {file ? `${file.name} · ${fmtSize(file.size)}` : "Scegli un PDF"}
                </button>
                {file && (
                  <button
                    type="button"
                    onClick={() => setShowLocalPreview(true)}
                    className="px-3 py-3 bg-lojack-blue-500/10 hover:bg-lojack-blue-500/20 border-2 border-lojack-blue-500/40 rounded-xl text-lojack-blue-300 flex items-center gap-1.5 text-sm shrink-0"
                    title="Anteprima locale (nessun upload)"
                    data-testid="upload-local-preview"
                  >
                    <Eye size={16} /> Anteprima
                  </button>
                )}
              </div>
              {file && (
                <p className="text-lojack-blue-400/70 text-[11px] mt-1.5 flex items-center gap-1">
                  <Eye size={11} className="shrink-0" />
                  Clicca <strong>Anteprima</strong> per vedere il PDF come lo vedrà il cliente <em>prima</em> di caricarlo — utile per catturare PDF vuoti/corrotti/password-protected senza sprecare l&apos;upload.
                </p>
              )}
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer p-3 bg-lojack-clause-500/10 border border-lojack-clause-500/30 rounded-xl">
            <input type="checkbox" checked={activateNow} onChange={e => setActivateNow(e.target.checked)} className="accent-lojack-clause-500" data-testid="upload-activate-now" />
            <span className="text-lojack-clause-200 text-xs">
              <strong>Attiva subito</strong> — sostituisce immediatamente la versione attualmente in produzione per <code>{docType}/{country || "?"}/{language}{isProductAware && product !== "__generic__" ? `/${product}` : ""}</code>. Deseleziona SOLO se stai caricando una bozza da attivare più tardi (⚠️ finché resta come bozza il cliente del paese vede errore 503).
            </span>
          </label>
          <button type="submit" disabled={submitting} className="btn-lojack w-full disabled:opacity-50" data-testid="upload-submit">
            {submitting ? "Caricamento..." : (activateNow ? "Carica e attiva" : "Carica come bozza")}
          </button>
        </form>
      </div>
      {showLocalPreview && file && localBlobUrl && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[300] flex items-center justify-center p-4" onClick={() => setShowLocalPreview(false)}>
          <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-5xl h-[90vh] border border-gray-700 overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="upload-local-preview-modal">
            <div className="flex items-center justify-between p-4 border-b border-gray-700 shrink-0">
              <div className="flex items-center gap-3">
                <Eye size={20} className="text-lojack-blue-400" />
                <div>
                  <h2 className="text-lg font-bold text-white">Anteprima locale (pre-upload)</h2>
                  <p className="text-gray-500 text-xs">
                    {file.name} · {fmtSize(file.size)}
                    <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-lojack-clause-500/15 text-lojack-clause-300 border border-lojack-clause-500/30">FILE LOCALE — non ancora caricato</span>
                  </p>
                </div>
              </div>
              <button onClick={() => setShowLocalPreview(false)} className="p-2 hover:bg-gray-800 rounded-lg" data-testid="upload-local-preview-close"><X size={20} className="text-gray-400" /></button>
            </div>
            <div className="flex-1 bg-[#0a0a0a] overflow-hidden">
              <iframe src={localBlobUrl} title={`Anteprima locale ${file.name}`} className="w-full h-full border-0" data-testid="upload-local-preview-iframe" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ──────────────────── Preview modal (client view) ────────────────────
//
// Fabrizio CR 03/07/2026 — Anteprima come cliente: fetch autenticato del
// PDF via axios (blob), URL.createObjectURL per l'iframe. Same-origin +
// X-Frame-Options: SAMEORIGIN → embed OK dentro il dominio della SPA.

// ─────────── RealCustomerPdfPane (used by "Anteprima con cliente reale") ───────────
//
// Sub-component that owns everything needed to render a single "PDF with
// real customer overlay" pane: its own customer picker, its own PDF fetch
// state, its own error/loading UI, and its own iframe. Duplicated when
// PreviewModal's compareMode is on so admin can validate business vs
// private layouts side-by-side (Fabrizio CR 23/07/2026).

const RealCustomerPdfPane = ({
  doc,
  getAuthHeader,
  customersList,
  side,                       // "single" | "A" | "B"
  selectedCustomerId,         // controlled by parent (PreviewModal)
  onSelectCustomer,           // callback (id: string) => void
}) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);
  const [previewMeta, setPreviewMeta] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const fetchUrl = `${API}/admin/legal-documents/${doc.id}/preview-with-real-customer${
    selectedCustomerId ? `?customer_id=${encodeURIComponent(selectedCustomerId)}` : ""
  }`;

  const openInNewTab = async () => {
    try {
      const res = await axios.get(fetchUrl, { ...getAuthHeader(), responseType: "blob" });
      const u = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(u, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch (e) {
      toast.error(`Download fallito: HTTP ${e.response?.status || "network"}`);
    }
  };

  useEffect(() => {
    let cancelled = false;
    let currentBlobUrl = null;
    setPdfLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await axios.get(fetchUrl, { ...getAuthHeader(), responseType: "blob" });
        if (cancelled) return;
        currentBlobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        setBlobUrl(currentBlobUrl);
        setPreviewMeta({
          customerId: res.headers["x-admin-preview-customer-id"] || "",
          customerName: res.headers["x-admin-preview-customer-name"] || "",
          matchedLanguage: res.headers["x-admin-preview-matched-language"] || "",
        });
      } catch (e) {
        if (cancelled) return;
        const status = e.response?.status;
        let detail = "";
        try {
          const blob = e.response?.data;
          if (blob && typeof blob.text === "function") {
            const txt = await blob.text();
            try { detail = JSON.parse(txt).detail || ""; } catch { detail = txt.slice(0, 200); }
          }
        } catch { /* noop */ }
        setError(`HTTP ${status || "network"}${detail ? " — " + detail : ""}`);
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (currentBlobUrl) URL.revokeObjectURL(currentBlobUrl);
    };
  }, [doc.id, getAuthHeader, fetchUrl]);

  const formatCustomerLabel = (c) => {
    const badge = c.is_business ? "🏢" : "👤";
    const vat = c.has_vat && !c.is_business ? " · P.IVA" : "";
    const veh = c.vehicle_hint ? ` · ${c.vehicle_hint}` : "";
    return `${badge} ${c.display_name}${vat}${veh}`;
  };

  const langLabel = LANG_LABELS[doc.language] || doc.language;
  const sideBadge = side === "A" ? "🅰️" : side === "B" ? "🅱️" : null;
  const testidSuffix = side === "single" ? "" : `-${side.toLowerCase()}`;

  return (
    <div className="flex flex-col min-h-0 flex-1" data-testid={`real-pdf-pane${testidSuffix}`}>
      {/* Customer picker toolbar */}
      {customersList && (customersList.total_matching + customersList.total_other) > 1 && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-700 bg-black/30 shrink-0" data-testid={`customer-picker-toolbar${testidSuffix}`}>
          {sideBadge && <span className="text-emerald-300 text-sm shrink-0">{sideBadge}</span>}
          <UserCheck size={14} className="text-emerald-400 shrink-0" />
          <label className="text-gray-400 text-xs whitespace-nowrap sr-only">Cambia cliente:</label>
          <select
            value={selectedCustomerId}
            onChange={(e) => onSelectCustomer(e.target.value)}
            disabled={pdfLoading}
            className="flex-1 min-w-0 bg-[#0a0a0a] border border-gray-700 rounded-lg px-2 py-1 text-xs text-gray-200 focus:border-emerald-500 focus:outline-none disabled:opacity-50"
            data-testid={`customer-picker-select${testidSuffix}`}
          >
            <option value="">🎯 Auto — primo cliente {langLabel}</option>
            {customersList.matching_language.length > 0 && (
              <optgroup label={`✅ Clienti in ${langLabel} (${customersList.total_matching})`}>
                {customersList.matching_language.map((c) => (
                  <option key={c.id} value={c.id}>{formatCustomerLabel(c)}</option>
                ))}
              </optgroup>
            )}
            {customersList.other_languages.length > 0 && (
              <optgroup label={`⚠️ Altre lingue (${customersList.total_other})`}>
                {customersList.other_languages.map((c) => (
                  <option key={c.id} value={c.id}>{formatCustomerLabel(c)} · {c.language}</option>
                ))}
              </optgroup>
            )}
          </select>
          {pdfLoading && (
            <Loader2 size={12} className="animate-spin text-emerald-300 shrink-0" data-testid={`customer-picker-loading${testidSuffix}`} />
          )}
        </div>
      )}

      {/* Badge strip */}
      {previewMeta?.customerName && (
        <div className="px-3 py-1.5 border-b border-gray-700 bg-emerald-500/5 shrink-0">
          <span className="text-[10px] font-semibold text-emerald-300" data-testid={`real-customer-badge${testidSuffix}`}>
            {sideBadge && <span className="mr-1">{sideBadge}</span>}
            Cliente reale: {previewMeta.customerName}
            {previewMeta.matchedLanguage === "fallback-any" && " · ⚠️ nessun cliente con questa lingua, usato fallback"}
            {previewMeta.matchedLanguage?.startsWith("explicit-") && ` · ⚠️ lingua cliente ≠ lingua PDF (${previewMeta.matchedLanguage.replace("explicit-", "")})`}
          </span>
        </div>
      )}

      {/* PDF iframe / error state */}
      <div className="flex-1 min-h-0 bg-[#0a0a0a] overflow-hidden">
        {error ? (
          <div className="h-full flex flex-col items-center justify-center p-4 text-center">
            <AlertTriangle size={28} className="text-red-400 mb-2" />
            <p className="text-red-300 font-semibold text-sm">Impossibile caricare</p>
            <p className="text-red-400/70 text-[10px] mt-1 font-mono max-w-full break-all">{error}</p>
            <button
              onClick={openInNewTab}
              type="button"
              className="mt-3 px-3 py-1.5 bg-lojack-blue-500/20 hover:bg-lojack-blue-500/30 border border-lojack-blue-500/40 rounded text-lojack-blue-200 text-xs inline-flex items-center gap-1.5"
              data-testid={`real-pane-fallback-open${testidSuffix}`}
            >
              <Download size={12} /> Apri PDF
            </button>
          </div>
        ) : !blobUrl ? (
          <div className="h-full flex items-center justify-center">
            <p className="text-gray-500 text-sm">Caricamento…</p>
          </div>
        ) : (
          <iframe src={blobUrl} title={`Anteprima ${doc.filename}${side !== "single" ? " " + side : ""}`} className="w-full h-full border-0" data-testid={`real-pane-iframe${testidSuffix}`} />
        )}
      </div>
    </div>
  );
};


const PreviewModal = ({ doc, onClose, getAuthHeader, withRealCustomer = false }) => {
  // Legacy single-blob state (used for the !withRealCustomer path)
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);

  // Real-customer picker infrastructure (fetched once at mount)
  const [customersList, setCustomersList] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  // Controlled state for the two panes (only used when withRealCustomer)
  const [customerAId, setCustomerAId] = useState("");   // "" = auto
  const [customerBId, setCustomerBId] = useState("");   // populated when compare toggled on

  // Suggested customer B when compare mode is toggled on: first customer
  // whose is_business differs from customer A (or the 2nd matching-language
  // customer if all are of the same kind). Used ONLY as initial value when
  // the checkbox is FIRST clicked ON — after that A and B are freely
  // controlled by the admin (including the ⇄ swap button).
  const suggestedCustomerB = useMemo(() => {
    if (!customersList) return "";
    const all = [...customersList.matching_language, ...customersList.other_languages];
    if (all.length < 2) return "";
    const first = all[0];
    const alt = all.find((c) => c.is_business !== first.is_business);
    return (alt || all[1] || {}).id || "";
  }, [customersList]);

  const toggleCompareMode = (e) => {
    const checked = e.target.checked;
    setCompareMode(checked);
    // Single-shot auto-init of B the first time the checkbox flips ON.
    // We do NOT use a useEffect because reactive re-runs would clobber
    // the ⇄ swap button when customerBId is legitimately reset to ""
    // (auto).
    if (checked && !customerBId && suggestedCustomerB) {
      setCustomerBId(suggestedCustomerB);
    }
  };

  const swapCustomers = () => {
    setCustomerAId(customerBId);
    setCustomerBId(customerAId);
  };

  const legacyFetchUrl = `${API}/admin/legal-documents/${doc.id}/download`;

  const openInNewTab = async () => {
    try {
      const res = await axios.get(legacyFetchUrl, { ...getAuthHeader(), responseType: "blob" });
      const u = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(u, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch (e) {
      toast.error(`Download fallito: HTTP ${e.response?.status || "network"}`);
    }
  };

  // Fetch the eligible customers ONCE when the modal opens in real-customer mode
  useEffect(() => {
    if (!withRealCustomer) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/admin/legal-documents/${doc.id}/eligible-customers`,
          getAuthHeader(),
        );
        if (!cancelled) setCustomersList(res.data);
      } catch (e) {
        if (!cancelled) toast.error(`Impossibile caricare la lista clienti: HTTP ${e.response?.status || "network"}`);
      }
    })();
    return () => { cancelled = true; };
  }, [doc.id, getAuthHeader, withRealCustomer]);

  // Legacy path — fetch the raw uploaded PDF once (only when NOT in real-customer mode)
  useEffect(() => {
    if (withRealCustomer) return;
    let cancelled = false;
    let currentBlobUrl = null;
    (async () => {
      try {
        const res = await axios.get(legacyFetchUrl, { ...getAuthHeader(), responseType: "blob" });
        if (cancelled) return;
        currentBlobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        setBlobUrl(currentBlobUrl);
      } catch (e) {
        if (cancelled) return;
        const status = e.response?.status;
        let detail = "";
        try {
          const blob = e.response?.data;
          if (blob && typeof blob.text === "function") {
            const txt = await blob.text();
            try { detail = JSON.parse(txt).detail || ""; } catch { detail = txt.slice(0, 200); }
          }
        } catch { /* noop */ }
        setError(`HTTP ${status || "network"}${detail ? " — " + detail : ""}`);
      }
    })();
    return () => {
      cancelled = true;
      if (currentBlobUrl) URL.revokeObjectURL(currentBlobUrl);
    };
  }, [doc.id, getAuthHeader, withRealCustomer, legacyFetchUrl]);

  const titleText = withRealCustomer
    ? "Anteprima con cliente reale"
    : "Anteprima come cliente";
  const TitleIcon = withRealCustomer ? UserCheck : Eye;

  const canCompare = withRealCustomer && customersList &&
    (customersList.total_matching + customersList.total_other) >= 2;

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[300] flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-[#1A1A1A] rounded-2xl w-full ${compareMode ? "max-w-7xl" : "max-w-5xl"} h-[90vh] border border-gray-700 overflow-hidden flex flex-col`} onClick={e => e.stopPropagation()} data-testid={withRealCustomer ? "preview-real-customer-modal" : "preview-client-modal"}>
        <div className="flex items-center justify-between p-4 border-b border-gray-700 shrink-0">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <TitleIcon size={20} className={`${withRealCustomer ? "text-emerald-400" : "text-lojack-blue-400"} shrink-0`} />
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold text-white">{titleText}</h2>
              <p className="text-gray-500 text-xs truncate">
                {doc.filename} · v{doc.version} · {LANG_LABELS[doc.language] || doc.language}
                {!withRealCustomer && doc.is_active
                  ? <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-lojack-blue-500/20 text-lojack-blue-300 border border-lojack-blue-500/40">ATTIVA — questo è ciò che vede il cliente</span>
                  : !withRealCustomer && <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-lojack-clause-500/15 text-lojack-clause-300 border border-lojack-clause-500/30">BOZZA — non ancora servita ai clienti</span>
                }
              </p>
            </div>
          </div>
          {/* Compare mode toggle — only in withRealCustomer mode with ≥2 customers */}
          {canCompare && (
            <label className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/40 rounded-lg text-emerald-200 text-xs cursor-pointer mr-2 shrink-0" data-testid="compare-mode-toggle-label">
              <input
                type="checkbox"
                checked={compareMode}
                onChange={toggleCompareMode}
                className="accent-emerald-500"
                data-testid="compare-mode-toggle"
              />
              <span>Confronta con altro cliente</span>
            </label>
          )}
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg shrink-0" data-testid="preview-close"><X size={20} className="text-gray-400" /></button>
        </div>

        {/* Body */}
        {withRealCustomer ? (
          <div className={`flex-1 min-h-0 ${compareMode ? "grid grid-cols-2 divide-x divide-gray-700 relative" : "flex flex-col"}`}>
            <RealCustomerPdfPane
              doc={doc}
              getAuthHeader={getAuthHeader}
              customersList={customersList}
              side={compareMode ? "A" : "single"}
              selectedCustomerId={customerAId}
              onSelectCustomer={setCustomerAId}
            />
            {compareMode && (
              <>
                <RealCustomerPdfPane
                  doc={doc}
                  getAuthHeader={getAuthHeader}
                  customersList={customersList}
                  side="B"
                  selectedCustomerId={customerBId}
                  onSelectCustomer={setCustomerBId}
                />
                {/* Swap A↔B — vertically-centered pill on the divider */}
                <button
                  type="button"
                  onClick={swapCustomers}
                  title="Scambia cliente 🅰️ ↔ 🅱️"
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg shadow-emerald-500/40 flex items-center justify-center transition-transform active:scale-95"
                  data-testid="swap-ab-button"
                  aria-label="Scambia clienti A e B"
                >
                  <span className="text-lg leading-none">⇄</span>
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="flex-1 bg-[#0a0a0a] overflow-hidden">
            {error ? (
              <div className="h-full flex flex-col items-center justify-center p-6 text-center">
                <AlertTriangle size={32} className="text-red-400 mb-3" />
                <p className="text-red-300 font-semibold">Impossibile caricare l&apos;anteprima</p>
                <p className="text-red-400/70 text-xs mt-1 font-mono">{error}</p>
                <p className="text-gray-500 text-xs mt-3 max-w-md">
                  Se il problema persiste in produzione, l&apos;iframe potrebbe essere bloccato da regole CSP/X-Frame-Options del proxy. Puoi comunque aprire il PDF in una nuova scheda:
                </p>
                <button
                  onClick={openInNewTab}
                  type="button"
                  className="mt-3 px-4 py-2 bg-lojack-blue-500/20 hover:bg-lojack-blue-500/30 border border-lojack-blue-500/40 rounded-lg text-lojack-blue-200 text-sm inline-flex items-center gap-2"
                  data-testid="preview-fallback-open"
                >
                  <Download size={14} /> Apri PDF in nuova scheda
                </button>
              </div>
            ) : !blobUrl ? (
              <div className="h-full flex items-center justify-center">
                <p className="text-gray-500 text-sm">Caricamento anteprima…</p>
              </div>
            ) : (
              <iframe src={blobUrl} title={`Anteprima ${doc.filename}`} className="w-full h-full border-0" data-testid="preview-iframe" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};


// ──────────────────── Compare modal (active vs draft) ─────────────────
//
// Fabrizio CR 03/07/2026 — Diff visivo affiancato tra la versione ATTIVA
// e una BOZZA. Due iframe autenticati, blob URLs indipendenti. Ideale per
// audit trail (Vercesi/Garante) e per catturare regressioni tipo
// "hanno cambiato il DPO ma dimenticato di aggiornare la data emissione".

const useAuthedBlobUrl = (docId, getAuthHeader) => {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let current = null;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/admin/legal-documents/${docId}/download`,
          { ...getAuthHeader(), responseType: "blob" }
        );
        if (cancelled) return;
        current = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        setUrl(current);
      } catch (e) {
        if (cancelled) return;
        const status = e.response?.status;
        let detail = "";
        try {
          const blob = e.response?.data;
          if (blob && typeof blob.text === "function") {
            const txt = await blob.text();
            try { detail = JSON.parse(txt).detail || ""; } catch { detail = txt.slice(0, 120); }
          }
        } catch { /* noop */ }
        setErr(`HTTP ${status || "network"}${detail ? " — " + detail : ""}`);
      }
    })();
    return () => {
      cancelled = true;
      if (current) URL.revokeObjectURL(current);
    };
  }, [docId, getAuthHeader]);
  return { url, err };
};

const CompareModal = ({ activeDoc, draftDoc, onClose, getAuthHeader }) => {
  const left = useAuthedBlobUrl(activeDoc.id, getAuthHeader);
  const right = useAuthedBlobUrl(draftDoc.id, getAuthHeader);

  const renderPane = (label, doc, state, testidBase, badgeClass, badgeLabel) => (
    <div className="flex-1 flex flex-col min-w-0 border-r border-gray-800 last:border-r-0">
      <div className="p-3 border-b border-gray-800 bg-[#0f0f0f] shrink-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${badgeClass}`}>{badgeLabel}</span>
          <span className="text-white text-sm font-semibold truncate">v{doc.version}</span>
        </div>
        <p className="text-gray-500 text-[11px] mt-1 truncate" title={doc.filename}>{doc.filename}</p>
        <p className="text-gray-600 text-[10px]">{label} · {fmtSize(doc.size_bytes)} · caricata {fmt(doc.uploaded_at)}</p>
      </div>
      <div className="flex-1 bg-[#0a0a0a] overflow-hidden">
        {state.err ? (
          <div className="h-full flex flex-col items-center justify-center p-4 text-center">
            <AlertTriangle size={24} className="text-red-400 mb-2" />
            <p className="text-red-300 text-xs">{state.err}</p>
          </div>
        ) : !state.url ? (
          <div className="h-full flex items-center justify-center"><p className="text-gray-500 text-xs">Caricamento…</p></div>
        ) : (
          <iframe src={state.url} title={`${badgeLabel} ${doc.filename}`} className="w-full h-full border-0" data-testid={`${testidBase}-iframe`} />
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[300] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-[95vw] h-[92vh] border border-gray-700 overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="compare-modal">
        <div className="flex items-center justify-between p-4 border-b border-gray-700 shrink-0">
          <div className="flex items-center gap-3">
            <History size={20} className="text-lojack-blue-400" />
            <div>
              <h2 className="text-lg font-bold text-white">Confronto versioni · Attiva vs Bozza</h2>
              <p className="text-gray-500 text-xs">
                Verifica cosa cambierà per il cliente se attivi la bozza a destra. Utile per audit trail e catch di regressioni (es. DPO cambiato senza aggiornare la data).
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg" data-testid="compare-close"><X size={20} className="text-gray-400" /></button>
        </div>
        <div className="flex-1 flex overflow-hidden">
          {renderPane(
            "Attualmente in produzione",
            activeDoc, left, "compare-active",
            "bg-lojack-blue-500/20 text-lojack-blue-300 border border-lojack-blue-500/40",
            "Attiva — cliente vede questo"
          )}
          {renderPane(
            "Nuova versione candidata",
            draftDoc, right, "compare-draft",
            "bg-lojack-clause-500/15 text-lojack-clause-300 border border-lojack-clause-500/30",
            "Bozza — se attivi, sostituirà l'attiva"
          )}
        </div>
      </div>
    </div>
  );
};


// ──────────────────── Orphan cleanup modal ────────────────────
// Fabrizio CR 04/07/2026 — UI wrapper per l'endpoint bulk cleanup dei
// record legal_documents rimasti "orfani" (file mancante su disco) dopo
// la migrazione a Emergent Object Storage. Doppio step di conferma:
//   1) al mount lancia dry_run e mostra breakdown per tipo/lingua +
//      lista degli attivi che verrebbero disattivati
//   2) l'admin conferma esplicitamente cliccando "Applica pulizia"
// Idempotente lato backend (rilanciarlo è safe). Solo canWrite lo apre.

const OrphanCleanupModal = ({ onClose, onDone, getAuthHeader }) => {
  const [dryRun, setDryRun] = useState(null);           // response dry_run
  const [loadingDry, setLoadingDry] = useState(true);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(null);         // response apply

  useEffect(() => {
    (async () => {
      setLoadingDry(true);
      try {
        const res = await axios.post(
          `${API}/admin/legal-documents/cleanup-orphans?dry_run=true`,
          {},
          getAuthHeader()
        );
        setDryRun(res.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Errore nella scansione preventiva");
        onClose();
      } finally { setLoadingDry(false); }
    })();
  }, [getAuthHeader, onClose]);

  const handleApply = async () => {
    setApplying(true);
    try {
      const res = await axios.post(
        `${API}/admin/legal-documents/cleanup-orphans`,
        {},
        getAuthHeader()
      );
      setApplied(res.data);
      toast.success(`Pulizia completata · ${res.data.total_orphans} record disattivati`, { duration: 6000 });
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante la pulizia");
    } finally { setApplying(false); }
  };

  const total = dryRun?.total_orphans || 0;
  const breakdown = dryRun?.breakdown || {};
  const activeOrphans = dryRun?.active_orphans_that_will_be_deactivated || [];
  const isDone = !!applied;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#1A1A1A] rounded-2xl w-full max-w-2xl border border-gray-700 overflow-hidden max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="orphan-cleanup-modal"
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/15 text-amber-400 flex items-center justify-center">
              <Sparkles size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Pulizia record orfani</h2>
              <p className="text-gray-500 text-xs">
                Disattiva i record che puntano a file non più presenti su disco né su Object Storage
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg" data-testid="orphan-cleanup-close">
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loadingDry && (
            <div className="flex items-center justify-center py-10 text-gray-400 gap-2" data-testid="orphan-cleanup-scanning">
              <Loader2 size={18} className="animate-spin" /> Scansione preventiva in corso…
            </div>
          )}

          {!loadingDry && !isDone && total === 0 && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-emerald-200 text-sm flex items-start gap-2" data-testid="orphan-cleanup-none">
              <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Nessun record orfano rilevato</p>
                <p className="text-emerald-300/80 text-xs mt-1">
                  Tutti i documenti in database puntano a file effettivamente presenti (su Object Storage o su filesystem locale).
                  Nessuna azione necessaria.
                </p>
              </div>
            </div>
          )}

          {!loadingDry && !isDone && total > 0 && (
            <>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-amber-200 text-sm">
                <p className="flex items-center gap-2 font-semibold">
                  <AlertTriangle size={16} /> Trovati {total} record orfani in {Object.keys(breakdown).length} combinazioni tipo/lingua
                </p>
                <p className="text-amber-300/80 text-xs mt-2">
                  L&apos;operazione imposta <code className="bg-black/30 px-1 rounded">is_active=false</code> su tutti i record elencati.
                  I file fisici sono già mancanti — non vengono toccati (sono già stati wipeati al deploy).
                  L&apos;operazione è tracciata nell&apos;audit log come <code className="bg-black/30 px-1 rounded">legal.orphan_cleanup_bulk</code> ed è idempotente.
                </p>
              </div>

              <div>
                <h3 className="text-white text-sm font-semibold mb-2">Breakdown per tipo · lingua</h3>
                <div className="bg-[#111] border border-gray-800 rounded-xl divide-y divide-gray-800" data-testid="orphan-cleanup-breakdown">
                  {Object.entries(breakdown).sort().map(([key, count]) => {
                    const [t, lang] = key.split("/");
                    const meta = TYPE_META[t] || TYPE_META.privacy;
                    const Icon = meta.icon;
                    return (
                      <div key={key} className="flex items-center justify-between px-3 py-2 text-sm">
                        <div className="flex items-center gap-2 text-gray-200">
                          <Icon size={14} className="text-gray-500" />
                          <span>{meta.label}</span>
                          <span className="text-gray-500">·</span>
                          <span className="text-gray-400">{LANG_LABELS[lang] || lang}</span>
                        </div>
                        <span className="px-2 py-0.5 rounded-full text-[11px] bg-amber-500/15 text-amber-300 border border-amber-500/30">
                          {count} record
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {activeOrphans.length > 0 && (
                <div>
                  <h3 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                    <AlertTriangle size={14} className="text-red-400" />
                    Versioni ATTIVE che verranno disattivate ({activeOrphans.length})
                  </h3>
                  <div className="bg-red-500/5 border border-red-500/30 rounded-xl p-3 space-y-1" data-testid="orphan-cleanup-active-list">
                    {activeOrphans.map((v, i) => (
                      <p key={i} className="text-red-200 text-xs font-mono">· {v}</p>
                    ))}
                    <p className="text-red-300/80 text-[11px] pt-2 border-t border-red-500/20 mt-2">
                      Questi documenti ATTIVI puntano a file spariti: il cliente riceve già HTTP 503 (Gate).
                      Disattivarli allinea il DB allo stato reale e rimuove i {'"fantasma"'} dalla UI.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {isDone && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-emerald-200 text-sm" data-testid="orphan-cleanup-done">
              <p className="flex items-center gap-2 font-semibold">
                <CheckCircle2 size={16} /> Pulizia completata
              </p>
              <p className="text-emerald-300/80 text-xs mt-2">{applied.message}</p>
              <p className="text-emerald-300/60 text-[11px] mt-1">
                Record disattivati: <strong>{applied.total_orphans}</strong>
                {applied.deactivated_active_versions?.length > 0 && (
                  <> · Versioni attive disattivate: <strong>{applied.deactivated_active_versions.length}</strong></>
                )}
              </p>
              <p className="text-emerald-300/80 text-xs mt-3">
                Ora carica i nuovi PDF nelle lingue scoperte dal Coverage — verranno salvati direttamente su Object Storage
                e sopravvivranno a qualsiasi redeploy.
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 p-4 border-t border-gray-700 bg-black/20">
          <p className="text-[11px] text-gray-500">
            {isDone
              ? "Operazione conclusa · chiudere per aggiornare la lista"
              : (total > 0 ? "Verifica il breakdown prima di applicare" : "Nulla da fare")}
          </p>
          <div className="flex items-center gap-2">
            {isDone ? (
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-200 text-sm font-medium"
                data-testid="orphan-cleanup-close-done"
              >
                Chiudi
              </button>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-xl bg-[#222] hover:bg-[#2a2a2a] border border-gray-700 text-gray-300 text-sm"
                  data-testid="orphan-cleanup-cancel"
                >
                  Annulla
                </button>
                {total > 0 && (
                  <button
                    onClick={handleApply}
                    disabled={applying || loadingDry}
                    className="px-4 py-2 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/50 text-amber-200 text-sm font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="orphan-cleanup-apply"
                  >
                    {applying ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                    {applying ? "Applicazione in corso…" : `Applica pulizia (${total})`}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};



// ──────────────────── pCloud restore modal ────────────────────
// Fabrizio CR 04/07/2026 — restore a legal PDF from a pCloud backup
// snapshot. Complements the P1 backup work: previously the admin could
// SEE that a backup existed but had no in-UI way to actually restore
// from it. Now one click, dry list, confirm, and the doc_id points at
// the restored binary. Activation stays a deliberate second step.

const PCloudRestoreModal = ({ onClose, onDone, getAuthHeader }) => {
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState([]);
  const [total, setTotal] = useState(0);
  const [restoring, setRestoring] = useState(null); // pcloud_fileid|null
  const [result, setResult] = useState(null);        // last restore payload
  const [filter, setFilter] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API}/admin/legal-documents/pcloud-backups?limit=300`,
          getAuthHeader()
        );
        setMonths(res.data.grouped_by_month || []);
        setTotal(res.data.total || 0);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Errore caricamento backup pCloud");
      } finally { setLoading(false); }
    })();
  }, [getAuthHeader]);

  const handleRestore = async (backup) => {
    if (!window.confirm(
      `Confermi il ripristino della versione "${backup.version}" ` +
      `(${backup.type} · ${backup.language}) dal backup pCloud del ` +
      `${(backup.backed_up_at || "").slice(0, 10)}?\n\n` +
      `Il documento verrà ricollegato al PDF recuperato ma NON verrà ` +
      `attivato automaticamente — dovrai riattivarlo esplicitamente.`
    )) return;

    setRestoring(backup.pcloud_fileid);
    try {
      const res = await axios.post(
        `${API}/admin/legal-documents/${backup.doc_id}/restore-from-pcloud`,
        { pcloud_fileid: backup.pcloud_fileid },
        getAuthHeader()
      );
      setResult(res.data);
      toast.success(
        `PDF ripristinato · ${res.data.restored_size_kb} KB` +
        (res.data.was_orphan ? " (record orfano recuperato)" : ""),
        { duration: 6000 }
      );
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante il ripristino");
    } finally { setRestoring(null); }
  };

  const filteredMonths = useMemo(() => {
    if (!filter.trim()) return months;
    const q = filter.trim().toLowerCase();
    return months.map(m => ({
      ...m,
      backups: (m.backups || []).filter(b =>
        (b.type || "").toLowerCase().includes(q) ||
        (b.language || "").toLowerCase().includes(q) ||
        (b.version || "").toLowerCase().includes(q) ||
        (b.doc_id || "").toLowerCase().includes(q)
      )
    })).filter(m => (m.backups || []).length > 0);
  }, [months, filter]);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#1A1A1A] rounded-2xl w-full max-w-4xl border border-gray-700 overflow-hidden max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="pcloud-restore-modal"
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-lojack-clause-500/15 text-lojack-clause-400 flex items-center justify-center">
              <History size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Ripristino da backup pCloud</h2>
              <p className="text-gray-500 text-xs">
                {total} snapshot disponibili · scegli la versione da ripristinare
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg" data-testid="pcloud-restore-close">
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        <div className="p-4 border-b border-gray-800">
          <input
            type="text"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filtra per tipo / lingua / versione / doc_id…"
            className="w-full bg-[#0F0F0F] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-lojack-clause-500 focus:outline-none"
            data-testid="pcloud-restore-filter"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-10 text-gray-400 gap-2">
              <Loader2 size={18} className="animate-spin" /> Caricamento snapshot pCloud…
            </div>
          )}

          {!loading && total === 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-amber-200 text-sm" data-testid="pcloud-restore-empty">
              <p className="flex items-center gap-2 font-semibold">
                <AlertTriangle size={16} /> Nessun backup pCloud disponibile
              </p>
              <p className="text-amber-300/80 text-xs mt-2">
                Il primo backup con PDF avviene automaticamente col prossimo
                ciclo di TelcaVoIP Cloud, oppure puoi lanciarlo manualmente da
                <code className="bg-black/30 px-1 rounded mx-1">Impostazioni → Backup</code>.
              </p>
            </div>
          )}

          {!loading && filteredMonths.map(month => (
            <div key={month.month_key} className="bg-[#0F0F0F] rounded-xl border border-gray-800" data-testid={`pcloud-restore-month-${month.month_key}`}>
              <div className="px-4 py-2.5 border-b border-gray-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar size={14} className="text-lojack-clause-400" />
                  <span className="text-white font-semibold text-sm">{month.month_key}</span>
                </div>
                <span className="text-xs text-gray-500">{month.count} snapshot</span>
              </div>
              <div className="divide-y divide-gray-800">
                {month.backups.map((b, idx) => {
                  const meta = TYPE_META[b.type] || TYPE_META.privacy;
                  const Icon = meta.icon;
                  const isRestoring = restoring === b.pcloud_fileid;
                  return (
                    <div
                      key={`${b.doc_id}-${b.pcloud_fileid}-${idx}`}
                      className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm hover:bg-black/20 transition-colors"
                      data-testid={`pcloud-restore-row-${b.doc_id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <Icon size={14} className="text-gray-500 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-white font-mono text-xs truncate">
                            <span className="text-lojack-clause-300">{meta.label}</span>
                            <span className="text-gray-600">·</span>
                            <span>{LANG_LABELS[b.language] || b.language}</span>
                            <span className="text-gray-600">·</span>
                            <span className="text-gray-400">v{b.version}</span>
                            {b.is_active && (
                              <span className="px-1.5 py-0.5 text-[10px] rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                                ATTIVO
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2">
                            <span>{b.size_kb} KB</span>
                            <span>·</span>
                            <span>backed up {(b.backed_up_at || "").slice(0, 19).replace("T", " ")}</span>
                            <span>·</span>
                            <span className="font-mono">fileid={b.pcloud_fileid}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRestore(b)}
                        disabled={isRestoring || restoring !== null}
                        className="shrink-0 px-3 py-1.5 rounded-lg bg-lojack-clause-500/15 hover:bg-lojack-clause-500/25 border border-lojack-clause-500/40 text-lojack-clause-200 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`pcloud-restore-btn-${b.doc_id}`}
                      >
                        {isRestoring ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                        {isRestoring ? "Ripristino…" : "Recupera"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {result && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-emerald-200 text-sm" data-testid="pcloud-restore-result">
              <p className="flex items-center gap-2 font-semibold">
                <CheckCircle2 size={16} /> Ripristinato — {result.restored_size_kb} KB
              </p>
              <p className="text-emerald-300/80 text-xs mt-2">{result.message}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-700 bg-black/20">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-[#222] hover:bg-[#2a2a2a] border border-gray-700 text-gray-300 text-sm"
            data-testid="pcloud-restore-cancel"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
};



// ──────────────────── Versions modal ────────────────────

const VersionsModal = ({ docType, language, product, versions, onClose, onChanged, getAuthHeader }) => {
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewRealDoc, setPreviewRealDoc] = useState(null);
  const [compareDraft, setCompareDraft] = useState(null);
  const activeVersion = useMemo(() => versions.find(v => v.is_active) || null, [versions]);
  const handleActivate = async (id) => {
    if (!window.confirm("Attivare questa versione?\n\nLa versione attualmente attiva (se presente) sarà sostituita per i clienti.")) return;
    try { await axios.post(`${API}/admin/legal-documents/${id}/activate`, {}, getAuthHeader()); toast.success("Versione attivata"); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa versione?\n\nL'operazione è loggata nell'audit di sicurezza.")) return;
    try { await axios.delete(`${API}/admin/legal-documents/${id}`, getAuthHeader()); toast.success("Versione eliminata"); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const handleNotify = async (v) => {
    const confirmMsg = `Inviare email di notifica ai clienti (lingua ${(v.language || "").toUpperCase()}) informandoli che è disponibile la nuova versione v${v.version}?\n\n• Solo clienti attivi (non cancellati) con paese compatibile\n• Massimo 1 notifica ogni 24h per documento\n• Ambiente PREVIEW: audit log salvato ma nessuna email realmente inviata`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await axios.post(`${API}/admin/legal-documents/${v.id}/notify`, {}, getAuthHeader());
      const d = res.data || {};
      if (d.status === "skipped") {
        toast.info(d.message || "Notifica saltata (preview)", { duration: 6000 });
      } else {
        toast.success(d.message || `Notifica inviata a ${d.sent}/${d.recipients_count} clienti`, { duration: 6000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio notifica");
    }
  };
  const meta = TYPE_META[docType] || TYPE_META.privacy;
  const productLabel = product
    ? (product === "__generic__" ? PRODUCT_LABELS["__generic__"] : (PRODUCT_LABELS[product] || product))
    : null;
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-3xl border border-gray-700 overflow-hidden max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="versions-modal">
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <div className="flex items-center gap-3"><History size={20} className="text-lojack-clause-400" /><div><h2 className="text-lg font-bold text-white">{meta.label} · {LANG_LABELS[language] || language}{productLabel ? ` · ${productLabel}` : ""}</h2><p className="text-gray-500 text-xs">{versions.length} versione/i caricate</p></div></div>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg"><X size={20} className="text-gray-400" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {versions.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">Nessuna versione caricata per questa combinazione</p>
          ) : (
            <div className="space-y-3" data-testid="versions-list">
              {versions.map(v => (
                <div key={v.id} className={`border rounded-xl p-3 ${v.is_active ? 'bg-lojack-blue-500/5 border-lojack-blue-500/40' : 'bg-[#111] border-gray-800'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white font-medium truncate">{v.filename}</p>
                        {v.is_active && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-lojack-blue-500/20 text-lojack-blue-300 border border-lojack-blue-500/40 flex items-center gap-1"><CheckCircle2 size={10} /> ATTIVA</span>}
                        {v.product && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-lojack-clause-500/15 text-lojack-clause-300 border border-lojack-clause-500/30">{v.product}</span>}
                        {v.type === "contract" && !v.product && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-500/15 text-blue-300 border border-blue-500/30">Generico</span>}
                      </div>
                      <p className="text-gray-400 text-xs mt-1"><span className="text-gray-500">Versione:</span> <strong>{v.version}</strong> · <span className="text-gray-500">Dimensione:</span> {fmtSize(v.size_bytes)}</p>
                      <p className="text-gray-500 text-xs mt-0.5"><Calendar size={10} className="inline mr-1" />Caricata da <strong>{v.uploaded_by}</strong> il {fmt(v.uploaded_at)}</p>
                      {v.activated_at && <p className="text-lojack-blue-400/70 text-[11px] mt-0.5">Attivata da {v.activated_by} il {fmt(v.activated_at)}</p>}
                      {v.notes && <p className="text-lojack-clause-300/80 text-[11px] mt-1.5 italic flex items-start gap-1"><AlertTriangle size={10} className="shrink-0 mt-0.5" />{v.notes}</p>}
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <button
                        onClick={() => setPreviewDoc(v)}
                        className="p-1.5 bg-lojack-blue-500/10 hover:bg-lojack-blue-500/20 border border-lojack-blue-500/40 rounded-lg text-lojack-blue-300"
                        title={v.is_active ? "Anteprima come cliente (versione attiva)" : "Anteprima come cliente (bozza — verifica prima di attivare)"}
                        data-testid={`preview-${v.id}`}
                      >
                        <Eye size={14} />
                      </button>
                      {v.type === "contract" && v.is_active && (
                        <button
                          onClick={() => setPreviewRealDoc(v)}
                          className="p-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/40 rounded-lg text-emerald-300"
                          title="Anteprima con cliente reale — carica il primo cliente attivo di questa lingua e mostra il PDF con i suoi dati sovrapposti (esattamente come lo vedrà lui)"
                          data-testid={`preview-real-${v.id}`}
                        >
                          <UserCheck size={14} />
                        </button>
                      )}
                      <a href={`${API}/admin/legal-documents/${v.id}/download`} target="_blank" rel="noreferrer" className="p-1.5 bg-[#1a1a1a] hover:bg-[#222] border border-gray-700 hover:border-blue-500/50 rounded-lg text-gray-400 hover:text-blue-400" title="Scarica"><Download size={14} /></a>
                      {v.is_active && (
                        <button
                          onClick={() => handleNotify(v)}
                          className="p-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/40 rounded-lg text-emerald-300"
                          title={`Notifica clienti (${(v.language || "").toUpperCase()}) — invia email con link alla nuova versione`}
                          data-testid={`notify-${v.id}`}
                        >
                          <Send size={14} />
                        </button>
                      )}
                      {!v.is_active && (
                        <>
                          {activeVersion && activeVersion.id !== v.id && (
                            <button
                              onClick={() => setCompareDraft(v)}
                              className="p-1.5 bg-lojack-blue-500/10 hover:bg-lojack-blue-500/20 border border-lojack-blue-500/40 rounded-lg text-lojack-blue-300"
                              title={`Confronta questa bozza (v${v.version}) con la versione attualmente attiva (v${activeVersion.version})`}
                              data-testid={`compare-${v.id}`}
                            >
                              <History size={14} />
                            </button>
                          )}
                          <button onClick={() => handleActivate(v.id)} className="p-1.5 bg-lojack-blue-500/10 hover:bg-lojack-blue-500/20 border border-lojack-blue-500/40 rounded-lg text-lojack-blue-300" title="Attiva questa versione" data-testid={`activate-${v.id}`}><CheckCircle2 size={14} /></button>
                          <button onClick={() => handleDelete(v.id)} className="p-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/40 rounded-lg text-red-300" title="Elimina" data-testid={`delete-${v.id}`}><Trash2 size={14} /></button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {previewDoc && (
        <PreviewModal
          doc={previewDoc}
          onClose={() => setPreviewDoc(null)}
          getAuthHeader={getAuthHeader}
        />
      )}
      {previewRealDoc && (
        <PreviewModal
          doc={previewRealDoc}
          onClose={() => setPreviewRealDoc(null)}
          getAuthHeader={getAuthHeader}
          withRealCustomer
        />
      )}
      {compareDraft && activeVersion && (
        <CompareModal
          activeDoc={activeVersion}
          draftDoc={compareDraft}
          onClose={() => setCompareDraft(null)}
          getAuthHeader={getAuthHeader}
        />
      )}
    </div>
  );
};

// ──────────────────── Coverage cell ────────────────────
// Fabrizio CR 04/07/2026 (Mara feedback) — tiles are keyed by SOC
// country (UK, IT, ES, ...) instead of language. `country` is the
// primary label + flag, `language` (derived via COUNTRY_TO_LANGUAGE
// on the parent) is the secondary label. Multiple countries can
// point to the same doc row (UK/IRL/GBZ → English).

const CoverageCell = ({ docType, language, country, active, versionsCount, acceptedLanguages, coverageByCountryLang, onUpload, onOpenVersions }) => {
  const isBilingual = Array.isArray(acceptedLanguages) && acceptedLanguages.length > 1;

  // ── Bilingue cell (Zorzutti CR 29/07/2026 fix) ──────────────────
  // Multilingual countries render a micro-grid: one mini-slot per
  // accepted official language, each with its own active/missing
  // state. Bypasses the "single language per country" limitation
  // that would show BE as fully covered when only FR is uploaded.
  if (isBilingual) {
    const [baseType, prodKey] = docType.includes("::") ? docType.split("::") : [docType, null];
    const langsCovered = acceptedLanguages.filter(l => {
      const cell = coverageByCountryLang?.[baseType]?.[country]?.[l];
      if (!cell) return false;
      if (prodKey) {
        return (typeof cell === "object" && !("version" in cell)) ? !!cell[prodKey] : false;
      }
      return true;
    });
    const anyActive = langsCovered.length > 0;
    const allActive = langsCovered.length === acceptedLanguages.length;
    const countryMeta = country ? COUNTRY_META[country] : null;
    return (
      <div
        className={`p-3 rounded-lg border transition-colors ${allActive ? "border-lojack-blue-500/40 bg-lojack-blue-500/5" : anyActive ? "border-lojack-clause-500/40 bg-lojack-clause-500/5" : "border-red-500/30 bg-red-500/5"}`}
        data-testid={`cell-bilingue-${docType}-${country}`}
      >
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="font-semibold text-white text-sm flex items-center gap-1.5">
            {countryMeta?.flag || "🏳️"} {country}
          </span>
          <span className={`text-[10px] font-semibold ${allActive ? "text-lojack-blue-400" : anyActive ? "text-lojack-clause-400" : "text-red-400"}`}>
            {langsCovered.length}/{acceptedLanguages.length}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {acceptedLanguages.map(l => {
            const cell = coverageByCountryLang?.[baseType]?.[country]?.[l];
            const activeForLang = prodKey && cell && typeof cell === "object" && !("version" in cell)
              ? cell[prodKey]
              : (!prodKey ? cell : null);
            const has = !!activeForLang;
            return (
              <button
                key={l}
                onClick={() => (has ? onOpenVersions(baseType, l, country) : onUpload(baseType, l, country))}
                title={has ? `${LANG_LABELS[l] || l.toUpperCase()} · v${activeForLang.version}` : `Manca la versione ${LANG_LABELS[l] || l.toUpperCase()} per ${country}`}
                className={`px-1.5 py-1 rounded text-[10px] font-semibold transition-colors ${has ? "bg-lojack-blue-500/15 text-lojack-blue-200 border border-lojack-blue-500/30 hover:bg-lojack-blue-500/25" : "bg-red-500/10 text-red-300 border border-red-500/30 hover:bg-red-500/15"}`}
                data-testid={`cell-bilingue-${docType}-${country}-${l}`}
              >
                {has ? "✓" : "⚠"} {l.toUpperCase()}
              </button>
            );
          })}
        </div>
        <p className="text-gray-500 text-[9px] mt-1.5 truncate">{LANG_LABELS[language] || language} (canonica)</p>
      </div>
    );
  }

  // ── Monolingua cell (behaviour prior to bilingue fix) ─────────
  const has = !!active;
  const hasAnyVersion = versionsCount > 0;
  const onClick = () => (hasAnyVersion ? onOpenVersions(docType, language, country) : onUpload(docType, language, country));
  const countryMeta = country ? COUNTRY_META[country] : null;
  const primaryLabel = country || language.toUpperCase();
  const secondaryLabel = country
    ? `${countryMeta?.flag || ""} ${LANG_LABELS[language] || language}`
    : LANG_LABELS[language] || language;

  const tooltipTitle = has
    ? `PDF ${LANG_LABELS[language] || language.toUpperCase()} attivo (v${active.version}) per ${primaryLabel}. Clic per gestire le versioni.`
    : hasAnyVersion
      ? `Bozza presente per ${primaryLabel}. Clic per attivare o caricare una nuova versione.`
      : `⚠️ Nessun PDF caricato per ${primaryLabel}. Clic per caricare.`;

  return (
    <button
      onClick={onClick}
      title={tooltipTitle}
      className={`text-left p-3 rounded-lg border transition-colors ${has ? "border-lojack-blue-500/40 bg-lojack-blue-500/5" : hasAnyVersion ? "border-lojack-clause-500/40 bg-lojack-clause-500/5 hover:bg-lojack-clause-500/10" : "border-red-500/30 bg-red-500/5 hover:bg-red-500/10"}`}
      data-testid={`cell-${docType}-${country || language}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-white flex items-center gap-1.5">
          {primaryLabel}
        </span>
        {has ? (
          <span className="text-lojack-blue-400 text-[10px] flex items-center gap-1"><CheckCircle2 size={10} />v{active.version}</span>
        ) : hasAnyVersion ? (
          <span className="text-lojack-clause-400 text-[10px]">Bozza</span>
        ) : (
          <span className="text-red-400 text-[10px] font-semibold">⚠️ Manca</span>
        )}
      </div>
      <p className="text-gray-500 text-[10px] mt-1 truncate">{secondaryLabel}</p>
      {versionsCount > 0 && <p className="text-lojack-clause-400/70 text-[9px] mt-1">{versionsCount} versione/i totali</p>}
    </button>
  );
};

// ──────────────────── Product-aware contract section ────────────────────
//
// Renders one mini-matrix per product variant (Generico + Passive 1/3/4),
// so the legal team can see at a glance whether each commercial product
// has its own active contract version per language.

const ProductSection = ({ docType, productKey, productLabel, supportedLanguages, socCountries, countryToLanguage, countryLanguages, coverageByCountryLang, allDocs, coverage, coverageByCountry, onUpload, onOpenVersions }) => {
  // Zorzutti CR 29/07/2026 — indexed docs BY COUNTRY (not language)
  // so the version-count badge on each tile shows only THIS country's
  // draft/active copies, not the whole language pool.
  const docsByCountry = useMemo(() => {
    const m = {};
    allDocs
      .filter(d => d.type === docType && (productKey === "__generic__" ? !d.product : d.product === productKey))
      .forEach(d => {
        const cc = d.country;
        if (!cc) return;  // legacy country=null rows no longer contribute
        (m[cc] = m[cc] || []).push(d);
      });
    return m;
  }, [allDocs, docType, productKey]);

  const activeForCountry = (cc) => {
    const cell = coverageByCountry?.[docType]?.[cc];
    if (!cell) return null;
    // Product-aware cell is a dict keyed by product (or "__generic__").
    if (typeof cell === "object" && !("version" in cell)) {
      return cell[productKey] || null;
    }
    // Fallback if backend returns a flat doc (e.g. legacy data)
    return productKey === "__generic__" ? cell : null;
  };

  // Iterate over SOC countries — each has its OWN doc row (Zorzutti CR
  // 29/07/2026). No more shared-language fallbacks: a missing country
  // is a missing country.
  const gridCountries = socCountries && socCountries.length ? socCountries : [];

  // Count coverage taking multilingual countries into account: a
  // bilingue country with 2 langs and 1 uploaded counts as 0.5 covered.
  // Simpler: count "fully covered" separately from "partial".
  const fullyCovered = gridCountries.filter(c => {
    const accepted = (countryLanguages || {})[c] || [(countryToLanguage || {})[c] || "it"];
    if (accepted.length === 1) return !!activeForCountry(c);
    // multilingual: every language must be covered
    return accepted.every(l => {
      const cell = coverageByCountryLang?.[docType]?.[c]?.[l];
      return !!(cell && (typeof cell !== "object" || cell.version || cell[productKey]));
    });
  }).length;

  return (
    <div className="bg-[#0E0E0E] border border-gray-800/70 rounded-xl p-4" data-testid={`product-section-${productKey}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="text-white font-semibold text-sm">{productLabel}</p>
          <p className="text-gray-500 text-[11px]">
            {fullyCovered} / {gridCountries.length} paesi SOC completi
            {fullyCovered < gridCountries.length && (
              <span className="ml-2 text-red-400/80">· {gridCountries.length - fullyCovered} paesi con doc mancante o incompleto</span>
            )}
          </p>
        </div>
        <button
          onClick={() => onUpload(docType, "it", productKey, "IT")}
          className="px-3 py-1.5 text-[11px] rounded-lg border border-blue-500/40 text-blue-300 hover:bg-blue-500/10 flex items-center gap-1.5"
          data-testid={`upload-${docType}-${productKey}`}
        >
          <Upload size={12} /> Carica per {productKey === "__generic__" ? "Generico" : productKey}
        </button>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-9 gap-2">
        {gridCountries.map(country => {
          const accepted = (countryLanguages || {})[country] || [(countryToLanguage || {})[country] || "it"];
          const canonicalLang = accepted[0];
          return (
            <CoverageCell
              key={country}
              docType={`${docType}::${productKey}`}
              language={canonicalLang}
              country={country}
              active={activeForCountry(country)}
              versionsCount={(docsByCountry[country] || []).length}
              acceptedLanguages={accepted}
              coverageByCountryLang={coverageByCountryLang}
              onUpload={(_t, lang, cc) => onUpload(docType, lang || canonicalLang, productKey, cc || country)}
              onOpenVersions={(_t, lang, cc) => onOpenVersions(docType, lang || canonicalLang, productKey, cc || country)}
            />
          );
        })}
      </div>
    </div>
  );
};

// ──────────────────── Type section ────────────────────

const TypeSection = ({ docType, supportedLanguages, supportedProducts, productAwareTypes, socCountries, countryToLanguage, countryLanguages, coverageByCountryLang, allDocs, coverage, coverageByCountry, onUpload, onOpenVersions }) => {
  const meta = TYPE_META[docType] || { label: docType, icon: FileCheck, accent: "blue" };
  const Icon = meta.icon;
  const isProductAware = (productAwareTypes || []).includes(docType);

  // Zorzutti CR 29/07/2026 — country-scoped index. Each SOC country
  // gets its own row (no more shared per-language pools).
  const docsByCountry = useMemo(() => {
    const m = {};
    allDocs.filter(d => d.type === docType && d.country).forEach(d => {
      (m[d.country] = m[d.country] || []).push(d);
    });
    return m;
  }, [allDocs, docType]);

  const gridCountries = socCountries && socCountries.length ? socCountries : [];

  // Coverage counters — fully covered means "every accepted language
  // of this country has a doc" (matters for bilingue countries).
  const fullyCovered = gridCountries.filter(c => {
    const accepted = (countryLanguages || {})[c] || [(countryToLanguage || {})[c] || "it"];
    if (accepted.length === 1) return !!coverageByCountry?.[docType]?.[c];
    return accepted.every(l => !!coverageByCountryLang?.[docType]?.[c]?.[l]);
  }).length;
  const totalActive = isProductAware
    ? gridCountries.reduce((acc, c) => {
        const cell = coverageByCountry?.[docType]?.[c];
        if (cell && typeof cell === "object" && !("version" in cell)) return acc + Object.keys(cell).length;
        return acc + (cell ? 1 : 0);
      }, 0)
    : fullyCovered;
  const totalSlots = isProductAware
    ? gridCountries.length * (1 + (supportedProducts || []).length)
    : gridCountries.length;
  const missing = totalSlots - totalActive;

  return (
    <div className="bg-[#111] border border-gray-800 rounded-2xl p-5" data-testid={`section-${docType}`}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-${meta.accent}-500/15 text-${meta.accent}-400`}><Icon size={20} /></div>
          <div>
            <h3 className="text-white font-bold">{meta.label}{isProductAware && <span className="ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-lojack-clause-500/20 text-lojack-clause-300 border border-lojack-clause-500/40">PER PRODOTTO</span>}</h3>
            <p className="text-gray-500 text-xs">
              {totalActive} / {totalSlots} {isProductAware ? "combinazioni (paese × prodotto)" : "paesi SOC"} con versione attiva
              {missing > 0 && (
                <span className="ml-2 text-red-400/90">· {missing} {isProductAware ? "combinazioni" : "paesi"} senza doc dedicato</span>
              )}
            </p>
          </div>
        </div>
        <button onClick={() => onUpload(docType, "it", undefined, "IT")} className="btn-lojack flex items-center gap-2 text-sm" data-testid={`upload-${docType}`}><Upload size={14} /> Carica</button>
      </div>

      {isProductAware ? (
        <div className="space-y-4">
          <ProductSection
            docType={docType}
            productKey="__generic__"
            productLabel={PRODUCT_LABELS["__generic__"]}
            supportedLanguages={supportedLanguages}
            socCountries={gridCountries}
            countryToLanguage={countryToLanguage}
            countryLanguages={countryLanguages}
            coverageByCountryLang={coverageByCountryLang}
            allDocs={allDocs}
            coverage={coverage}
            coverageByCountry={coverageByCountry}
            onUpload={onUpload}
            onOpenVersions={onOpenVersions}
          />
          {(supportedProducts || []).map(p => (
            <ProductSection
              key={p}
              docType={docType}
              productKey={p}
              productLabel={PRODUCT_LABELS[p] || p}
              supportedLanguages={supportedLanguages}
              socCountries={gridCountries}
              countryToLanguage={countryToLanguage}
              countryLanguages={countryLanguages}
              coverageByCountryLang={coverageByCountryLang}
              allDocs={allDocs}
              coverage={coverage}
              coverageByCountry={coverageByCountry}
              onUpload={onUpload}
              onOpenVersions={onOpenVersions}
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-9 gap-2">
          {gridCountries.map(country => {
            const accepted = (countryLanguages || {})[country] || [(countryToLanguage || {})[country] || "it"];
            const canonicalLang = accepted[0];
            return (
              <CoverageCell
                key={country}
                docType={docType}
                language={canonicalLang}
                country={country}
                active={coverageByCountry?.[docType]?.[country]}
                versionsCount={(docsByCountry[country] || []).length}
                acceptedLanguages={accepted}
                coverageByCountryLang={coverageByCountryLang}
                onUpload={(_t, lang, cc) => onUpload(docType, lang || canonicalLang, undefined, cc || country)}
                onOpenVersions={(_t, lang, cc) => onOpenVersions(docType, lang || canonicalLang, undefined, cc || country)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

// ──────────────────── Main page ────────────────────

// Zorzutti CR 22/02/2026 — La sezione "Allegati" (type=annex) è stata
// nascosta dalla UI perché nel flusso di firma contratto attuale gli
// Allegati 1/2/3 vengono generati inline dentro il PDF contratto
// (services/contract_v2_renderer.py::_build_annex_1/_2/_3) e NON dal
// CMS. La coverage 0/18 dava un'impressione allarmistica in demo.
// Il backend (endpoint, modello, endpoint /admin/legal-documents/*,
// resolver in routes/legal_public.py) rimane INTATTO e pronto per
// futuri upload di allegati supplementari (manuali utente localizzati,
// garanzie country-specific, addendum contrattuali, ecc.). Per
// ri-attivare la sezione, basta rimuovere "annex" da questo set.
const HIDDEN_DOC_TYPES = new Set(["annex"]);

/**
 * Coverage-gap banner (Zorzutti CR 29/07/2026, enhancement).
 *
 * Renders one alert row per doc-type with 1+ SOC country missing an
 * active doc. Click → smoothly scrolls to the first red tile of that
 * type so the admin can start uploading immediately. Silent (renders
 * nothing) when every type is fully covered.
 */
const MissingCoverageBanners = ({ coverageByCountry, socCountries, supportedTypes }) => {
  const gaps = useMemo(() => {
    if (!coverageByCountry || !socCountries?.length) return [];
    return supportedTypes.map(t => {
      const perCountry = coverageByCountry[t] || {};
      const missing = socCountries.filter(cc => !perCountry[cc]);
      return { type: t, missing };
    }).filter(g => g.missing.length > 0);
  }, [coverageByCountry, socCountries, supportedTypes]);

  if (gaps.length === 0) return null;

  const scrollToFirstMissing = (docType, firstMissingCountry) => {
    // ``contract`` is product-aware — tile testid uses the compound
    // ``contract::__generic__``. Other types use the plain docType.
    const testId = docType === "contract"
      ? `cell-contract::__generic__-${firstMissingCountry}`
      : `cell-${docType}-${firstMissingCountry}`;
    const el = document.querySelector(`[data-testid="${testId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Brief highlight so the target is unmistakable after the scroll.
      el.style.transition = "box-shadow 0.4s ease";
      el.style.boxShadow = "0 0 0 3px rgba(239, 68, 68, 0.7)";
      setTimeout(() => { el.style.boxShadow = ""; }, 1800);
    }
  };

  return (
    <div className="space-y-2 mb-6" data-testid="coverage-gaps-banners">
      {gaps.map(({ type, missing }) => {
        const meta = TYPE_META[type] || { label: type };
        const first = missing[0];
        return (
          <button
            key={type}
            onClick={() => scrollToFirstMissing(type, first)}
            className="w-full text-left bg-red-500/10 hover:bg-red-500/15 border border-red-500/40 rounded-2xl p-4 flex items-center justify-between gap-4 transition-colors group"
            data-testid={`coverage-gap-banner-${type}`}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-9 h-9 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={18} className="text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-red-100 font-semibold text-sm">
                  ⚠️ {missing.length} pae{missing.length === 1 ? "se senza" : "si senza"}{" "}
                  {meta.label.toLowerCase()} dedicat{missing.length === 1 ? "o/a" : "i/e"}
                </p>
                <p className="text-red-300/80 text-xs mt-0.5 truncate">
                  Manca: {missing.slice(0, 12).join(", ")}
                  {missing.length > 12 && ` … +${missing.length - 12} altri`}
                </p>
              </div>
            </div>
            <span className="text-red-300 text-xs font-semibold whitespace-nowrap group-hover:text-red-200">
              Vai al primo ({first}) →
            </span>
          </button>
        );
      })}
    </div>
  );
};

const LegalDocumentsPage = () => {
  const [docs, setDocs] = useState([]);
  const [coverage, setCoverage] = useState({});
  const [coverageByCountry, setCoverageByCountry] = useState({});
  const [coverageByCountryLang, setCoverageByCountryLang] = useState({});
  const [supportedTypes, setSupportedTypes] = useState([]);
  const [supportedLanguages, setSupportedLanguages] = useState([]);
  const [supportedProducts, setSupportedProducts] = useState([]);
  const [productAwareTypes, setProductAwareTypes] = useState([]);
  // SOC country view (Mara CR 04/07/2026) — populated from /coverage.
  // Fallback to hardcoded lists if the backend hasn't been redeployed yet,
  // so the UI never breaks even during a partial rollout.
  const [socCountries, setSocCountries] = useState([
    "UK", "IT", "ES", "FR", "NL", "BE", "PT", "PL", "SE", "NO",
    "FI", "DK", "DE", "CZ", "GR", "CH", "GBZ", "IRL",
  ]);
  const [countryToLanguage, setCountryToLanguage] = useState({
    UK: "en", IRL: "en", GBZ: "en", IT: "it", ES: "es",
    FR: "fr", BE: "fr", NL: "nl", PT: "pt", PL: "pl",
    SE: "sv", NO: "no", FI: "fi", DK: "da", DE: "de",
    CH: "de", CZ: "cs", GR: "el",
  });
  // Zorzutti CR 29/07/2026 bilingue — {country: [accepted ISO langs]}
  // Multilingual countries have >1 language (BE: fr/nl/de, CH: de/fr/it,
  // FI: fi/sv, IRL: en/ga, GBZ: en/es). Single-lang countries have 1.
  // Populated from ``/coverage.country_languages`` with a hardcoded
  // fallback matching ``services/country_language.py`` so the UI
  // renders during a partial backend rollout.
  const [countryLanguages, setCountryLanguages] = useState({
    BE: ["fr", "nl", "de"],
    CH: ["de", "fr", "it"],
    FI: ["fi", "sv"],
    IRL: ["en", "ga"],
    GBZ: ["en", "es"],
  });
  const [loading, setLoading] = useState(true);
  const [uploadCtx, setUploadCtx] = useState(null);
  const [versionsCtx, setVersionsCtx] = useState(null);
  const [showOrphanCleanup, setShowOrphanCleanup] = useState(false);
  const [showPCloudRestore, setShowPCloudRestore] = useState(false);
  const { getAuthHeader, user } = useAuth();

  const canWrite = user?.role === 'super_admin' || user?.role === 'admin';

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/legal-documents`, getAuthHeader()),
        axios.get(`${API}/admin/legal-documents/coverage`, getAuthHeader()),
      ]);
      setDocs(a.data.documents);
      setSupportedTypes(a.data.supported_types);
      setSupportedLanguages(a.data.supported_languages);
      setSupportedProducts(a.data.supported_products || []);
      setProductAwareTypes(a.data.product_aware_types || []);
      setCoverage(b.data.matrix);
      setCoverageByCountry(b.data.matrix_by_country || {});
      setCoverageByCountryLang(b.data.matrix_by_country_lang || {});
      if (b.data.country_languages && typeof b.data.country_languages === "object") {
        setCountryLanguages(b.data.country_languages);
      }
      // If the backend exposes the SOC mapping, use it (source of truth).
      // Otherwise keep the hardcoded fallback so the UI still renders.
      if (Array.isArray(b.data.soc_countries) && b.data.soc_countries.length) {
        setSocCountries(b.data.soc_countries);
      }
      if (b.data.country_to_language && typeof b.data.country_to_language === "object") {
        setCountryToLanguage(b.data.country_to_language);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fabrizio CR 07/08/2026 — Deep-link dal Legal Preview:
  // ``/admin/legal?upload=contract&lang=nl&country=BE`` apre automaticamente
  // il modal upload con i valori pre-compilati, così quando Mara clicca
  // "Carica ora →" dal warning CMS del Legal Preview trova il form pronto.
  // Consuma i query params una sola volta al mount (dopo fetchAll ha
  // popolato supportedTypes/countryLanguages) e li rimuove dall'URL per
  // evitare che un refresh riaperga il modal.
  const _searchParams = useSearchParams()[0];
  const _navigate = useNavigate();
  useEffect(() => {
    if (loading) return;
    const uploadType = _searchParams.get("upload");
    if (!uploadType || !canWrite) return;
    // Only accept types the backend actually supports (defence-in-depth
    // vs handcrafted URLs that don't match our whitelist).
    if (supportedTypes.length && !supportedTypes.includes(uploadType)) {
      toast.error(`Tipo documento non supportato: ${uploadType}`);
      _navigate("/admin/legal", { replace: true });
      return;
    }
    const lang = (_searchParams.get("lang") || "").toLowerCase();
    const country = (_searchParams.get("country") || "").toUpperCase();
    const product = _searchParams.get("product") || "__generic__";
    setUploadCtx({
      docType: uploadType,
      language: lang,
      country: country,
      product,
    });
    toast.success(
      `Compila il form per caricare il template ${uploadType} mancante` +
      (country ? ` (paese ${country}${lang ? `, lingua ${lang}` : ""})` : "")
    );
    // Strip query string so a refresh doesn't reopen the modal.
    _navigate("/admin/legal", { replace: true });
  }, [loading, _searchParams, canWrite, supportedTypes, _navigate]);

  // Fabrizio CR 06/07/2026 — Deep-link scroll dal Health Score widget:
  // ``/admin/legal#section-contract?highlight=fr`` scrolla alla sezione
  // del tipo documento e evidenzia la lingua target per 3 secondi.
  useEffect(() => {
    if (loading) return;
    const hashRaw = window.location.hash.slice(1); // strip leading '#'
    if (!hashRaw) return;
    const [anchor, queryStr] = hashRaw.split("?");
    if (!anchor?.startsWith("section-")) return;
    const el = document.querySelector(`[data-testid="${anchor}"]`);
    if (el) {
      // Small delay to let the grid finish rendering the tiles.
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        const params = new URLSearchParams(queryStr || "");
        const highlight = params.get("highlight");
        if (highlight) {
          const langUpper = highlight.toUpperCase();
          // Cells are keyed by SOC country (2-letter uppercase). For most
          // languages the country code === language code (IT, FR, DE, ES,
          // NL, PL, PT, ...); when it doesn't match (e.g., ``en`` → GB/IE)
          // querySelector returns null and we simply skip highlighting.
          const langTile = el.querySelector(
            `[data-testid^="cell-"][data-testid$="-${langUpper}"]`
          );
          if (langTile) {
            langTile.classList.add("ring-2", "ring-lojack-blue-400", "ring-offset-2", "ring-offset-[#111]");
            setTimeout(() => langTile.classList.remove("ring-2", "ring-lojack-blue-400", "ring-offset-2", "ring-offset-[#111]"), 3000);
          }
        }
      }, 350);
    }
  }, [loading]);

  const handleUpload = (docType, language, productKey, country) => {
    if (!canWrite) { toast.error("Permessi insufficienti"); return; }
    setUploadCtx({
      docType,
      language,
      product: productKey || "__generic__",
      country: country || "",
    });
  };
  const handleOpenVersions = (docType, language, productKey, country) => setVersionsCtx({
    docType, language, product: productKey || null, country: country || null,
  });

  const versionsForCtx = useMemo(() => {
    if (!versionsCtx) return [];
    return docs.filter(d => {
      if (d.type !== versionsCtx.docType) return false;
      if (versionsCtx.country && d.country !== versionsCtx.country) return false;
      if (!versionsCtx.country && d.language !== versionsCtx.language) return false;
      // When product is filtered, also match product (or generic if "__generic__")
      if (versionsCtx.product === null) return true;
      if (versionsCtx.product === "__generic__") return !d.product;
      return d.product === versionsCtx.product;
    });
  }, [docs, versionsCtx]);

  return (
    <div className="p-6 max-w-7xl mx-auto animate-fade-in" data-testid="legal-docs-page">
      <LegalCoverageGapsAlert showLink={false} />
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-blue-500/15 text-blue-400 flex items-center justify-center"><FileCheck size={24} /></div>
          <div>
            <h1 className="text-3xl font-bold text-white">Documenti Legali</h1>
            <p className="text-gray-400 text-sm">Gestione versioni multilingua di Informativa Privacy, Contratto e Allegati</p>
          </div>
        </div>
        {canWrite && (
          <button
            onClick={() => setShowOrphanCleanup(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 text-xs font-medium transition-colors"
            title="Scansiona il database e disattiva i record che puntano a file spariti (post-deploy wipeout)"
            data-testid="orphan-cleanup-open"
          >
            <Sparkles size={14} />
            Pulizia record orfani
          </button>
        )}
        {canWrite && (
          <button
            onClick={() => setShowPCloudRestore(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-lojack-clause-500/10 hover:bg-lojack-clause-500/20 border border-lojack-clause-500/30 text-lojack-clause-300 text-xs font-medium transition-colors"
            title="Ripristina un PDF da uno snapshot pCloud (utile per rollback selettivi)"
            data-testid="pcloud-restore-open"
          >
            <History size={14} />
            Ripristino da backup
          </button>
        )}
        {!canWrite && (
          <div className="bg-lojack-clause-500/10 border border-lojack-clause-500/30 rounded-xl px-3 py-2 text-lojack-clause-300 text-xs flex items-center gap-2" data-testid="read-only-banner">
            <Eye size={14} /> Modalità sola lettura · serve ruolo Admin o Super Admin per modificare
          </div>
        )}
      </div>

      <div className="bg-blue-500/5 border border-blue-500/30 rounded-2xl p-4 mb-6 text-blue-200 text-sm">
        <p><strong className="text-blue-300">Come funziona</strong> — Per ogni combinazione tipo + lingua, puoi caricare più versioni del PDF nel tempo. Esattamente <strong>una versione</strong> è {'"attiva"'} ed è quella che vedono i clienti. Le altre restano archiviate per audit/rollback. Quando carichi una nuova versione puoi decidere se attivarla subito o lasciarla come bozza. Prima di attivare, usa l&apos;icona {'"👁"'} per vedere il PDF <strong>esattamente come lo vedrà il cliente</strong>. Le lingue senza upload attivo generano <strong>HTTP 503</strong> per il cliente finché non attivi il PDF (nessun fallback silenzioso).</p>
      </div>

      {/* Zorzutti CR 29/07/2026 — coverage gap banners.
          One row per doc-type with un-covered SOC countries, so Legal
          knows AT-A-GLANCE where to focus (instead of scrolling all
          the way down to spot the red tiles). Clicking a banner
          scrolls to the first missing country tile of that type. */}
      <MissingCoverageBanners
        coverageByCountry={coverageByCountry}
        socCountries={socCountries}
        supportedTypes={supportedTypes.filter(t => !HIDDEN_DOC_TYPES.has(t))}
      />

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : (
        <div className="space-y-5">
          {/* CMS coverage dashboard — Zorzutti feedback 22/02/2026:
              collapse the three per-doc-type widgets into a single
              summary banner. Details available on demand via
              "Espandi", preserving power-user visibility without
              dominating the page. */}
          <div data-testid="cms-coverage-section">
            <CmsCoverageCollapsibleSection onChanged={fetchAll} />
          </div>
          {supportedTypes.filter(t => !HIDDEN_DOC_TYPES.has(t)).map(t => (
            <TypeSection
              key={t}
              docType={t}
              supportedLanguages={supportedLanguages}
              supportedProducts={supportedProducts}
              productAwareTypes={productAwareTypes}
              socCountries={socCountries}
              countryToLanguage={countryToLanguage}
              countryLanguages={countryLanguages}
              coverageByCountryLang={coverageByCountryLang}
              allDocs={docs}
              coverage={coverage}
              coverageByCountry={coverageByCountry}
              onUpload={handleUpload}
              onOpenVersions={handleOpenVersions}
            />
          ))}
        </div>
      )}

      {uploadCtx && (
        <UploadModal
          defaultType={uploadCtx.docType}
          defaultLang={uploadCtx.language}
          defaultCountry={uploadCtx.country}
          defaultProduct={uploadCtx.product}
          supportedTypes={supportedTypes}
          supportedLanguages={supportedLanguages}
          supportedProducts={supportedProducts}
          productAwareTypes={productAwareTypes}
          countryToLanguage={countryToLanguage}
          countryLanguages={countryLanguages}
          onClose={() => setUploadCtx(null)}
          onUploaded={fetchAll}
          getAuthHeader={getAuthHeader}
        />
      )}
      {versionsCtx && (
        <VersionsModal
          docType={versionsCtx.docType}
          language={versionsCtx.language}
          product={versionsCtx.product}
          versions={versionsForCtx}
          onClose={() => setVersionsCtx(null)}
          onChanged={() => { fetchAll(); }}
          getAuthHeader={getAuthHeader}
        />
      )}
      {showOrphanCleanup && (
        <OrphanCleanupModal
          onClose={() => setShowOrphanCleanup(false)}
          onDone={fetchAll}
          getAuthHeader={getAuthHeader}
        />
      )}
      {showPCloudRestore && (
        <PCloudRestoreModal
          onClose={() => setShowPCloudRestore(false)}
          onDone={fetchAll}
          getAuthHeader={getAuthHeader}
        />
      )}
    </div>
  );
};

export default LegalDocumentsPage;
